import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listConciergeSites, listOrgUsers } from "@/lib/admin/concierge-api";
import { StatusBadge } from "@/components/admin/concierge/StatusBadge";
import { CreateConciergeDialog } from "./CreateConciergeDialog";

export default function ConciergeList() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: sites = [], isLoading } = useQuery({
    queryKey: ["concierge", "admin", "sites", orgId],
    queryFn: () => listConciergeSites(orgId),
    staleTime: 30_000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["concierge", "admin", "org-users", orgId],
    queryFn: () => listOrgUsers(orgId),
    staleTime: 30_000,
  });
  const handleFor = (uid: string) =>
    users.find((u) => u.user_id === uid)?.handle ?? uid.slice(0, 8);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Concierge</h1>
          <p className="text-sm text-muted-foreground">
            Per-user mini-sites with a voice advisor and curated resources.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Create Concierge
        </Button>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin opacity-60" />
        </div>
      ) : sites.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground">
          No Concierge sites yet.
        </div>
      ) : (
        <div className="border border-border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Target user</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{handleFor(s.user_id)}</TableCell>
                  <TableCell>{s.title ?? "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(s.updated_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/app/admin/concierge/${s.id}/insights`}>Insights</Link>
                    </Button>
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/app/admin/concierge/${s.id}`}>Edit</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateConciergeDialog open={createOpen} onOpenChange={setCreateOpen} orgId={orgId} />
    </div>
  );
}
